import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActiveScanBanner from "@/components/auto-sourcing/ActiveScanBanner";
import {
  activeRunChoices,
  isTerminalStatus,
  readScanState,
  resolveRestoredRun,
  writeScanState,
} from "@/lib/autoSourcing/scanPersistence";
import { toast } from "sonner";
import {
  Check,
  ChevronDown,
  Loader2,
  Play,
  RotateCw,
  Save,
  Trash2,
  Wand2,
} from "lucide-react";
import RetailerPicker, { canSelectSource } from "@/components/auto-sourcing/RetailerPicker";
import CategoryPicker, { isSelectionReady } from "@/components/auto-sourcing/CategoryPicker";
import ManualCategoryUrls, {
  manualUrlsReady,
  parseCategoryUrls,
} from "@/components/auto-sourcing/ManualCategoryUrls";
import RetailerScopePanels, {
  allRetailerScopesReady,
  emptySlice,
} from "@/components/auto-sourcing/RetailerScopePanels";
import GlassCombobox from "@/components/auto-sourcing/GlassCombobox";
import RetailerLogo from "@/components/auto-sourcing/RetailerLogo";
import PresetBar, { ConfirmDelete } from "@/components/auto-sourcing/PresetBar";
import AutoSourcingResults from "@/components/auto-sourcing/AutoSourcingResults";
import { analyzerHref } from "@/lib/analyzeQuery";
import {
  generatedPresetName,
  normalizePersistedProfile,
  sortPresets,
  stableStringify,
  systemPresetSignature,
  uniquePresetName,
} from "@/lib/autoSourcing/presets";
import {
  autoSearchName,
  serializeAutoSourcingProfile,
} from "@/lib/autoSourcing/serializeProfile";
import {
  CUSTOM_QUERY_REQUIRED_ERROR,
  friendlyAutoSourcingError,
} from "@/lib/autoSourcing/errors";
import "@/components/auto-sourcing/AutoSourcing.css";
import {
  EMPTY_AUTO_SOURCING_PROFILE,
  BUILTIN_PRESETS,
} from "@/lib/autoSourcing/defaultProfile";
import {
  MAX_SCAN_DURATION_MINUTES,
  durationPartsToMinutes,
  minutesToDurationParts,
} from "@/components/auto-sourcing/runSummaryFormat";
import {
  clearNewlyReady,
  refreshRetailerDirectory,
  useRetailerDirectory,
} from "@/lib/autoSourcing/retailerDirectory";
import {
  listManualCategories,
  listSourceCategories,
  listAutoSourcingProfiles,
  createAutoSourcingProfile,
  updateAutoSourcingProfile,
  cloneAutoSourcingProfile,
  runAutoSourcingProfile,
  deleteAutoSourcingProfile,
  listAutoSourcingRuns,
  getAutoSourcingRun,
  getAutoSourcingCapabilities,
  listAutoSourcingResults,
  resumeAutoSourcingRun,
  pauseAutoSourcingRun,
  stopAutoSourcingRun,
} from "@/lib/autoSourcing/api";

const ACCORDIONS = [
  { key: "match_strategy", label: "Match Strategy" },
  { key: "adjustments", label: "Source Price Adjustments" },
  { key: "exclusions", label: "Remove / Exclusions" },
  { key: "title_rules", label: "Source Titles" },
  { key: "costs", label: "Costs / Fees" },
  { key: "profitability", label: "Profit & ROI" },
  { key: "demand", label: "Walmart Demand" },
  { key: "competition", label: "Competition" },
  { key: "availability", label: "Other Filters" },
  // Scan Limits lives in the always-visible Basic section (duration + budget).
];

/** Minutes / Hours / Days selector with a hard 72-hour continuous-session ceiling. */
function ScanDurationField({ minutes, onMinutesChange, onInvalidChange }) {
  const synced = minutesToDurationParts(minutes);
  const [amount, setAmount] = useState(synced.amount);
  const [unit, setUnit] = useState(synced.unit);

  useEffect(() => {
    const p = minutesToDurationParts(minutes);
    setAmount(p.amount);
    setUnit(p.unit);
    onInvalidChange?.(false);
  }, [minutes]); // eslint-disable-line react-hooks/exhaustive-deps -- sync draft when profile minutes change

  const apply = (nextAmount, nextUnit) => {
    const n = Math.max(1, Number(nextAmount) || 1);
    setAmount(n);
    setUnit(nextUnit);
    const mins = durationPartsToMinutes(n, nextUnit);
    const over = mins > MAX_SCAN_DURATION_MINUTES;
    onInvalidChange?.(over);
    if (!over) onMinutesChange(mins);
  };

  const over = durationPartsToMinutes(amount, unit) > MAX_SCAN_DURATION_MINUTES;

  return (
    <Field label="Scan duration" helper="Continuous session limit. You can resume afterward.">
      <div className="as-duration-row">
        <input
          className="as-input"
          type="number"
          min={1}
          data-testid="as-scan-duration-amount"
          value={amount}
          onChange={(e) => apply(e.target.value, unit)}
        />
        <select
          className="as-input as-duration-unit"
          data-testid="as-scan-duration-unit"
          value={unit}
          onChange={(e) => apply(amount, e.target.value)}
        >
          <option value="minutes">Minutes</option>
          <option value="hours">Hours</option>
          <option value="days">Days</option>
        </select>
      </div>
      {over ? (
        <div className="as-duration-error" data-testid="as-duration-error">
          Maximum continuous scan duration is 72 hours. You can resume the scan
          afterward.
        </div>
      ) : null}
    </Field>
  );
}

function cloneProfile() {
  return JSON.parse(JSON.stringify(EMPTY_AUTO_SOURCING_PROFILE));
}

const STATUS_LABELS = {
  paused: "Paused",
  queued: "Queued",
  running: "Running",
  collecting: "Running",
  matching: "Running",
  verifying: "Running",
  completed: "Completed",
  partial: "Completed",
  failed: "Error",
};

function _statusLabel(status) {
  return STATUS_LABELS[status] || "Paused";
}

// Truth-preserving status styling: paused is neutral gray, failed is red,
// queued is amber/neutral, running/completed are emerald. Never show green
// "running" styling for a paused/queued/failed backend state.
function _statusClass(status) {
  if (!status) return "paused";
  const s = String(status).toLowerCase();
  if (s === "paused") return "paused";
  if (s === "failed" || s === "error" || s === "cancelled") return "error";
  if (s === "queued") return "queued";
  return "active";
}

// Run-list badge colors (truth-preserving): failed→red, queued→amber,
// running/completed→emerald, everything else neutral gray.
function runStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "failed" || s === "error" || s === "cancelled") return "error";
  if (s === "queued") return "queued";
  if (["running", "collecting", "matching", "verifying", "completed", "partial"].includes(s)) {
    return "operational";
  }
  return "catalog";
}

let _fieldSeq = 0;

function Field({ label, helper, children }) {
  const id = React.useMemo(() => `as-field-${++_fieldSeq}`, []);
  return (
    <div className="as-field">
      <label htmlFor={id}>{label}</label>
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { id })
          : child
      )}
      {helper ? <div className="as-helper">{helper}</div> : null}
    </div>
  );
}

function NumberField({ label, value, onChange, placeholder, helper, min, max, step, disabled }) {
  return (
    <Field label={label} helper={helper}>
      <input
        className="as-input"
        type="number"
        min={min}
        max={max}
        step={step}
        value={value ?? ""}
        placeholder={disabled ? "Not available yet" : placeholder}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
            return;
          }
          const n = Number(raw);
          if (!Number.isFinite(n)) {
            onChange(null);
            return;
          }
          // `min`/`max` on the input are only advisory — the browser happily
          // accepts a typed "-1". Clamp here so the form cannot hold a value the
          // server will reject (a negative product limit used to fail the whole
          // search with an unactionable validation error).
          let next = n;
          if (typeof min === "number" && next < min) next = min;
          if (typeof max === "number" && next > max) next = max;
          onChange(next);
        }}
      />
    </Field>
  );
}

// Saved profiles: honor the explicit category_selection; older profiles are
// mapped from their execution fields (no selection ran as All).
function restoreCategorySelection(scope = {}) {
  const sel = scope.category_selection;
  if (sel && ["all", "category", "custom", "urls"].includes(sel.mode)) {
    if (sel.mode === "custom") {
      return { mode: "custom", user_query: sel.user_query || scope.custom_category_query || "", resolver: "raw_query" };
    }
    return { ...sel };
  }
  const cat = scope.selected_categories?.[0];
  if (cat?.category_id) return { mode: "category", category_id: cat.category_id, name: cat.name, url: cat.url };
  const cq = (scope.custom_category_query || "").trim();
  if (cq) return { mode: "custom", user_query: cq, resolver: "raw_query" };
  return { mode: "all" };
}

export default function AutoSourcing() {
  const nav = useNavigate();
  // Restored so returning to Auto Sourcing lands where the customer left off
  // instead of silently resetting to Search with no active job visible.
  const [tab, setTab] = useState(() => readScanState().tab || "search");
  const [mode, setMode] = useState("basic");
  const [durationInvalid, setDurationInvalid] = useState(false);
  const directory = useRetailerDirectory();
  const sources = directory.items;
  const sourcesLoading = directory.status === "loading" || directory.status === "idle";
  const sourcesError = directory.status === "error" ? "Couldn't load retailers." : null;
  const loadSources = refreshRetailerDirectory;
  const readyNotice = directory.newlyReady.length
    ? `${directory.newlyReady.length} ${directory.newlyReady.length === 1 ? "retailer" : "retailers"} became available`
    : null;
  useEffect(() => {
    if (!directory.newlyReady.length) return undefined;
    const t = setTimeout(clearNewlyReady, 10_000);
    return () => clearTimeout(t);
  }, [directory.newlyReady]);
  const [profile, setProfile] = useState(cloneProfile());
  const [categoryUrlText, setCategoryUrlText] = useState("");
  const [presets, setPresets] = useState([]);
  const [profilesError, setProfilesError] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsError, setRunsError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const submittingRef = useRef(false);
  const saving = pendingAction !== null;
  const [status, setStatus] = useState({ key: "paused", label: "Paused" });
  const [capabilities, setCapabilities] = useState({});
  const [fieldCapabilities, setFieldCapabilities] = useState({});
  const [capabilitiesError, setCapabilitiesError] = useState(null);
  const [results, setResults] = useState([]);
  const [resultsError, setResultsError] = useState(null);
  const [activeRunId, setActiveRunId] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [sourceCategories, setSourceCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  // Active SYSTEM preset (null while a saved preset is loaded).
  const [activePresetId, setActivePresetId] = useState("all");
  const [systemBaseline, setSystemBaseline] = useState(() => systemPresetSignature(cloneProfile()));
  // Loaded saved preset: { profile_id, baseline } where baseline is the
  // normalized persisted profile it was loaded/saved with.
  const [loadedPreset, setLoadedPreset] = useState(null);
  const [profilesLoaded, setProfilesLoaded] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [presetBusy, setPresetBusy] = useState(false);
  const defaultHandledRef = useRef(false);

  const loadCapabilities = useCallback(async () => {
    setCapabilitiesError(null);
    try {
      const data = await getAutoSourcingCapabilities();
      setCapabilities(data?.profile_sections || {});
      setFieldCapabilities(data?.profile_fields || {});
    } catch {
      setCapabilities({});
      setFieldCapabilities({});
      setCapabilitiesError("Unable to load profile capabilities.");
    }
  }, []);

  const loadProfiles = useCallback(async () => {
    setProfilesError(null);
    try {
      const data = await listAutoSourcingProfiles();
      setPresets(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setPresets([]);
      setProfilesError("Unable to load saved profiles.");
    } finally {
      setProfilesLoaded(true);
    }
  }, []);

  const activeRunIdRef = useRef(activeRunId);
  activeRunIdRef.current = activeRunId;
  const selectedRunRef = useRef(selectedRun);
  selectedRunRef.current = selectedRun;

  // Stable identity: the mount effect depends on this callback, so it must not
  // change when the active/selected run changes (that refetched everything).
  const loadRuns = useCallback(async () => {
    setRunsError(null);
    try {
      const data = await listAutoSourcingRuns();
      const items = Array.isArray(data?.items) ? data.items : [];
      setRuns(items);
      // Restore the job the customer was actually watching. A persisted id wins
      // even after it finishes; only when nothing was persisted do we fall back
      // to the newest in-flight scan. This is what stops the app from silently
      // swapping in a different job after a navigation.
      const persisted = readScanState();
      const restored = resolveRestoredRun(items, persisted.activeRunId);
      const target = restored.run;
      // Only a non-terminal job is "active". A finished job is restored as the
      // SELECTED job (so its Results stay reachable) but must not keep polling.
      const targetIsActive = Boolean(target?.run_id) && !isTerminalStatus(target?.status);
      if (targetIsActive) {
        const alreadyWatching =
          activeRunIdRef.current && String(activeRunIdRef.current) === String(target.run_id);
        if (!alreadyWatching) {
          setActiveRunId(target.run_id);
          setStatus({ key: target.status || "queued", label: _statusLabel(target.status || "queued") });
        }
      } else if (!activeRunIdRef.current) {
        setStatus({ key: "paused", label: "Paused" });
      }
      // Selected (Results) job follows the restored job unless the customer has
      // already opened one in this session.
      if (target?.run_id && !selectedRunRef.current) {
        setSelectedRun(target);
      }
      // Keep storage in sync with what is actually on screen.
      writeScanState({
        activeRunId: targetIsActive ? target.run_id : activeRunIdRef.current || null,
        selectedRunId: (selectedRunRef.current?.run_id) || target?.run_id || null,
      });
    } catch {
      setRuns([]);
      setRunsError("Unable to load runs.");
    }
  }, []);

  useEffect(() => {
    void loadProfiles();
    void loadRuns();
    void loadCapabilities();
  }, [loadProfiles, loadRuns, loadCapabilities]);

  // Keep the chosen tab durably so leaving Auto Sourcing and coming back does
  // not drop the customer on Search with no idea which scan is running.
  useEffect(() => {
    writeScanState({ tab });
  }, [tab]);

  // Remember which job the customer is actually looking at.
  useEffect(() => {
    if (selectedRun?.run_id) {
      writeScanState({ selectedRunId: selectedRun.run_id });
    }
  }, [selectedRun?.run_id]);

  const loadResults = useCallback(async (params = {}) => {
    setResultsError(null);
    try {
      // Opportunities API defaults to limit=24 — for a run, page through the
      // repo ceiling (100/page) so WINNING cards are not silently truncated.
      // Durable run-products pagination is the primary Results grid path.
      const pageSize = Math.min(100, Number(params.limit) || 100);
      if (params.run_id) {
        const items = [];
        let page = 1;
        let total = Infinity;
        while (items.length < total && page <= 50) {
          const data = await listAutoSourcingResults({
            ...params,
            page,
            limit: pageSize,
          });
          const batch = Array.isArray(data?.items) ? data.items : [];
          total = Number(data?.total ?? data?.summary?.total_leads) || batch.length;
          items.push(...batch);
          if (!batch.length || batch.length < pageSize) break;
          page += 1;
        }
        setResults(items);
        return;
      }
      const data = await listAutoSourcingResults({ ...params, limit: pageSize });
      setResults(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setResults([]);
      setResultsError("Unable to load results.");
    }
  }, []);

  // Auto-load results for the selected run when the Results tab opens (and on
  // selectedRun change). 0 provider calls.
  useEffect(() => {
    if (tab === "results" && selectedRun?.run_id) {
      void loadResults({ run_id: selectedRun.run_id });
    }
  }, [tab, selectedRun, loadResults]);

  // Poll the active run status (bounded, backend-derived, 0 provider calls).
  //
  // Resilient to transient failures: a dropped read backs off and retries
  // instead of killing the live channel. Durable Results cards poll their own
  // page, so this loop never re-pages opportunities on every tick.
  useEffect(() => {
    if (!activeRunId) return undefined;
    let cancelled = false;
    let timer = null;
    let failures = 0;
    const poll = async () => {
      try {
        const run = await getAutoSourcingRun(activeRunId);
        if (cancelled) return;
        failures = 0;
        setStatus({ key: run?.status || "queued", label: _statusLabel(run?.status || "queued") });
        if (run?.run_id) {
          setRuns((prev) => prev.map((r) => (r.run_id === run.run_id ? { ...r, ...run } : r)));
          setSelectedRun((prev) => (prev?.run_id === run.run_id ? { ...prev, ...run } : prev));
        }
        const terminal = ["completed", "partial", "failed", "cancelled"].includes(run?.status);
        if (terminal) {
          setActiveRunId(null);
          void loadRuns();
          void loadProfiles();
          if (tab === "results" && run?.run_id) {
            void loadResults({ run_id: run.run_id });
          }
          return;
        }
      } catch {
        // Transient read failure — keep the last known status and retry with
        // backoff so live updates resume on their own.
        failures += 1;
      }
      if (cancelled) return;
      const delay = Math.min(30_000, 5000 * Math.max(1, failures + 1));
      timer = setTimeout(poll, delay);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeRunId, loadRuns, loadProfiles, loadResults, tab]);

  const selectedSource = sources.find((s) => s.source_id === profile.scope.source_ids[0]);
  const selectedOperational = selectedSource && canSelectSource(selectedSource);
  // Persisted per-retailer capability (automatic | manual | pending); absent
  // when the backend has category modes switched off (previous UI applies).
  const discoveryMode = selectedSource?.category_discovery_mode || null;
  const manualUrls = useMemo(() => parseCategoryUrls(categoryUrlText), [categoryUrlText]);
  const [savedManualUrls, setSavedManualUrls] = useState([]);

  // Saved category URLs for a manual retailer (DB read only, never crawls).
  useEffect(() => {
    setSavedManualUrls([]);
    if (discoveryMode !== "manual" || !selectedOperational || !selectedSource?.source_id) return undefined;
    let cancelled = false;
    listManualCategories(selectedSource.source_id)
      .then((data) => {
        if (!cancelled) setSavedManualUrls(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedSource?.source_id, discoveryMode, selectedOperational]);

  // Load the persisted category catalog when a category_catalog retailer is
  // selected (DB read only, 0 provider calls).
  useEffect(() => {
    const mode = selectedSource?.category_support?.mode;
    setCategoriesLoaded(false);
    if (mode !== "category_catalog" || !selectedSource?.source_id) {
      setSourceCategories([]);
      return;
    }
    let cancelled = false;
    setCategoriesLoading(true);
    listSourceCategories(selectedSource.source_id)
      .then((data) => {
        if (cancelled) return;
        setSourceCategories(Array.isArray(data?.items) ? data.items : []);
        setCategoriesLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setSourceCategories([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSource?.source_id, selectedSource?.category_support?.mode]);

  // Customer-facing Category field state (backend category_support is the
  // source of truth; no fake category capability is ever introduced). Every
  // runnable retailer uses the same single-select picker: All, any real
  // backend categories, and Custom search.
  const categoryState = (() => {
    if (!selectedSource) {
      return {
        kind: "none",
        disabled: true,
        helper: "Choose a retailer to see available search options.",
      };
    }
    if (!selectedOperational) {
      return {
        kind: "unavailable-retailer",
        disabled: true,
        helper: "Select an available retailer to start sourcing.",
      };
    }
    if (discoveryMode === "pending") {
      return { kind: "pending", disabled: true, helper: null };
    }
    const cs = selectedSource.category_support;
    // Manual discovery means no proven category catalog — but search_query
    // retailers (e.g. Target) still support keyword search. URLs are an
    // optional shortcut, not the only path.
    if (discoveryMode === "manual") {
      if (cs?.supported && (cs?.mode === "search_query" || cs?.mode === "category_catalog")) {
        return {
          kind: "manual_optional_urls",
          searchKind: cs.mode,
          disabled: false,
          helper: "Use keyword search, or optionally paste category URLs below.",
        };
      }
      return { kind: "manual", disabled: false, helper: null };
    }
    if (cs?.supported && cs?.mode === "category_catalog") {
      return { kind: "category_catalog", disabled: false, helper: null };
    }
    if (cs?.supported) {
      return { kind: "search_query", disabled: false, helper: null };
    }
    return {
      kind: "not-available",
      disabled: true,
      helper: "More search options will be available for this retailer soon.",
    };
  })();

  const categorySelection = profile.scope.category_selection || null;
  // A saved category that is no longer in the active backend list is shown by
  // name with a warning — never silently replaced.
  const selectedCategoryInactive =
    categorySelection?.mode === "category" &&
    (categoryState.kind === "category_catalog" ||
      (categoryState.kind === "manual_optional_urls" && categoryState.searchKind === "category_catalog")) &&
    categoriesLoaded &&
    !sourceCategories.some((c) => c.category_id === categorySelection.category_id);
  const urlsReady = manualUrlsReady(manualUrls, selectedSource?.domain);
  const searchSelectionReady =
    !categoryState.disabled &&
    categorySelection?.mode !== "urls" &&
    isSelectionReady(categorySelection, { categoryAvailable: !selectedCategoryInactive });
  const multiRetailer = (profile.scope.source_ids || []).length > 1;
  const sourcesById = useMemo(() => {
    const m = new Map();
    sources.forEach((s) => m.set(s.source_id, s));
    return m;
  }, [sources]);
  const singleCategoryReady =
    categoryState.kind === "manual"
      ? urlsReady
      : categoryState.kind === "manual_optional_urls"
        ? searchSelectionReady || urlsReady
        : searchSelectionReady;
  const categoryReady = multiRetailer
    ? allRetailerScopesReady(
        profile.scope.source_ids,
        profile.scope.source_scope || {},
        sourcesById,
        {},
      )
    : singleCategoryReady;

  const changeManualUrls = (text) => {
    setCategoryUrlText(text);
    const urls = parseCategoryUrls(text);
    if (!urls.length) {
      // Empty optional URLs — clear urls-mode so keyword search can take over.
      setProfile((p) => {
        if (p.scope.category_selection?.mode !== "urls") return p;
        return {
          ...p,
          scope: {
            ...p.scope,
            category_selection: null,
            selected_categories: [],
            categories: [],
            custom_category_query: null,
          },
        };
      });
      return;
    }
    setScope({ category_selection: { mode: "urls" }, selected_categories: [], categories: [], custom_category_query: null });
  };

  // Single source of truth: category_selection drives the execution fields
  // (selected_categories for a real category, custom_category_query for
  // Custom search, neither for All).
  const changeCategorySelection = (selection) => {
    // Keyword / catalog selection wins over pasted URLs.
    setCategoryUrlText("");
    const sid = profile.scope.source_ids?.[0];
    const withSlice = (patch) => {
      if (!sid) return patch;
      return {
        ...patch,
        source_scope: {
          ...(profile.scope.source_scope || {}),
          [sid]: {
            ...(profile.scope.source_scope?.[sid] || emptySlice()),
            category_selection: patch.category_selection,
            selected_categories: patch.selected_categories || [],
            custom_category_query: patch.custom_category_query ?? null,
            category_urls: patch.category_urls || [],
          },
        },
      };
    };
    if (!selection) {
      setScope(withSlice({ category_selection: null, selected_categories: [], categories: [], custom_category_query: null }));
      return;
    }
    if (selection.mode === "category") {
      const cat = sourceCategories.find((c) => c.category_id === selection.category_id) || selection;
      setScope(
        withSlice({
          category_selection: { mode: "category", category_id: cat.category_id, name: cat.name, url: cat.url },
          selected_categories: [{ category_id: cat.category_id, name: cat.name, url: cat.url }],
          categories: [],
          custom_category_query: null,
        }),
      );
      return;
    }
    if (selection.mode === "custom") {
      const q = selection.user_query || "";
      setScope(
        withSlice({
          category_selection: { mode: "custom", user_query: q, resolver: "raw_query" },
          selected_categories: [],
          categories: [],
          custom_category_query: q.trim() ? q : null,
        }),
      );
      return;
    }
    setScope(withSlice({ category_selection: { mode: "all" }, selected_categories: [], categories: [], custom_category_query: null }));
  };

  const setCustomQueryValue = (value) =>
    changeCategorySelection({ mode: "custom", user_query: value, resolver: "raw_query" });

  const changeRetailers = (next) => {
    const ids = (Array.isArray(next) ? next : next ? [next] : []).filter(Boolean);
    const prev = profile.scope.source_ids || [];
    const same =
      ids.length === prev.length && ids.every((id, i) => id === prev[i]);
    if (same) return;
    setCategoryUrlText("");
    const prevScope = profile.scope.source_scope || {};
    const nextScope = {};
    ids.forEach((sid) => {
      if (prevScope[sid]) {
        nextScope[sid] = { ...prevScope[sid] };
      } else if (ids.length > 1) {
        // Multi-retailer: new retailers default to All (still editable per panel).
        nextScope[sid] = emptySlice();
      } else {
        // Single-retailer: require an explicit category/search choice.
        nextScope[sid] = { ...emptySlice(), category_selection: null };
      }
    });
    // Dropping to a single retailer: promote that slice to top-level fields.
    const promoted = ids.length === 1 ? nextScope[ids[0]] : null;
    const clearingSingle = ids.length <= 1;
    setScope({
      source_ids: ids,
      source_scope: nextScope,
      category_selection: clearingSingle ? (promoted?.category_selection ?? null) : null,
      selected_categories: clearingSingle ? (promoted?.selected_categories ?? []) : [],
      categories: [],
      custom_category_query: clearingSingle ? (promoted?.custom_category_query ?? null) : null,
      category_urls: clearingSingle ? (promoted?.category_urls ?? []) : [],
    });
    if (ids.length > 0) {
      setLimits({ max_sources: Math.max(ids.length, Number(profile.scan_limits?.max_sources) || 1) });
    }
  };

  const changeRetailerSlice = (sourceId, slice) => {
    setProfile((p) => {
      const map = { ...(p.scope.source_scope || {}), [sourceId]: slice };
      const ids = p.scope.source_ids || [];
      // Keep top-level in sync when only one retailer is selected.
      if (ids.length === 1 && ids[0] === sourceId) {
        return {
          ...p,
          scope: {
            ...p.scope,
            source_scope: map,
            category_selection: slice.category_selection,
            selected_categories: slice.selected_categories || [],
            custom_category_query: slice.custom_category_query,
            category_urls: slice.category_urls || [],
          },
        };
      }
      return { ...p, scope: { ...p.scope, source_scope: map } };
    });
  };

  const setScope = (patch) => setProfile((p) => ({ ...p, scope: { ...p.scope, ...patch } }));
  const setMatch = (patch) =>
    setProfile((p) => ({ ...p, match_strategy: { ...p.match_strategy, ...patch } }));
  const setProfit = (patch) =>
    setProfile((p) => ({ ...p, profitability: { ...p.profitability, ...patch } }));
  const setDemand = (patch) =>
    setProfile((p) => ({ ...p, demand: { ...p.demand, ...patch } }));
  const setAdjust = (patch) =>
    setProfile((p) => ({ ...p, adjustments: { ...p.adjustments, ...patch } }));
  const setLimits = (patch) =>
    setProfile((p) => ({ ...p, scan_limits: { ...p.scan_limits, ...patch } }));
  const setSchedule = (patch) =>
    setProfile((p) => ({ ...p, schedule: { ...p.schedule, ...patch } }));
  const setExclusions = (patch) =>
    setProfile((p) => ({ ...p, exclusions: { ...p.exclusions, ...patch } }));
  const setTitleRules = (patch) =>
    setProfile((p) => ({ ...p, title_rules: { ...p.title_rules, ...patch } }));
  const setCosts = (patch) => setProfile((p) => ({ ...p, costs: { ...p.costs, ...patch } }));
  const setCompetition = (patch) =>
    setProfile((p) => ({ ...p, competition: { ...p.competition, ...patch } }));
  const setAvailability = (patch) =>
    setProfile((p) => ({ ...p, availability: { ...p.availability, ...patch } }));

  // A section is editable only if the backend reports it "enforced". Unknown
  // sections are treated as disabled (fail-closed) so a stored-only control
  // never silently ignores user input.
  const sectionEnabled = (key) => capabilities[key] === "enforced";
  // A section is "configured" (openable/editable) when enforced OR partial
  // (some fields enforced, others unavailable). Individual fields are still
  // gated by fieldEnabled/fieldUnavailable in the basic UI.
  const sectionConfigured = (key) =>
    capabilities[key] === "enforced" || capabilities[key] === "partial";

  // Field-level capability: a single control is enabled when the backend
  // reports "enforced" or "enforced_when_observed" (the latter is enforced but
  // fails closed when the real signal is missing). "unavailable"/missing stays
  // disabled with truthful wording.
  const fieldEnabled = (key) => {
    const s = fieldCapabilities[key];
    return s === "enforced" || s === "enforced_when_observed";
  };
  const fieldUnavailable = (key) => fieldCapabilities[key] === "unavailable";

  const applyPreset = (preset) => {
    // Deep-merge the preset patch onto the CURRENT profile so nested sections
    // (match_strategy, demand, exclusions) are patched, not replaced, while
    // the user's other inputs (name, scope) are preserved.
    const next = { ...profile };
    for (const [section, value] of Object.entries(preset.patch || {})) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        next[section] = { ...(next[section] || {}), ...value };
      } else {
        next[section] = value;
      }
    }
    if (loadedPreset) next.name = "";
    setProfile(next);
    setLoadedPreset(null);
    setActivePresetId(preset.id);
    setSystemBaseline(systemPresetSignature(next));
    toast.success(`Applied “${preset.name}” preset`);
  };

  const toggleAccordion = (key) => {
    setProfile((p) => {
      const next = { ...p };
      const open = next._open || {};
      next._open = { ...open, [key]: !open[key] };
      return next;
    });
  };

  // Search name is optional; the serializer names it from retailer + category.
  const autoName = () => autoSearchName(selectedSource, categorySelection);

  const buildPayload = () => {
    const useUrlsOnly =
      categoryState.kind === "manual" ||
      (categoryState.kind === "manual_optional_urls" &&
        (categorySelection?.mode === "urls" || (!searchSelectionReady && urlsReady)));
    return serializeAutoSourcingProfile(
      useUrlsOnly
        ? {
            ...profile,
            scope: {
              ...profile.scope,
              category_selection: { mode: "urls" },
              selected_categories: [],
              categories: [],
              custom_category_query: null,
            },
          }
        : profile,
      {
        mode,
        source: selectedSource,
        categoryUrls: useUrlsOnly || categorySelection?.mode === "urls" ? manualUrls : [],
      }
    );
  };

  const rememberProfile = (created) => {
    if (!created?.profile_id) return;
    setPresets((prev) => [created, ...prev.filter((p) => p.profile_id !== created.profile_id)]);
  };

  const currentPersisted = normalizePersistedProfile(buildPayload());
  const presetDirty =
    Boolean(loadedPreset) && stableStringify(currentPersisted) !== stableStringify(loadedPreset.baseline);
  const systemModified =
    !loadedPreset && Boolean(activePresetId) && systemPresetSignature(profile) !== systemBaseline;
  const sortedPresets = useMemo(() => sortPresets(presets), [presets]);
  const loadedPresetDoc = loadedPreset ? presets.find((p) => p.profile_id === loadedPreset.profile_id) : null;

  // The saved doc becomes the loaded, clean preset (payload = what was sent).
  const adoptSavedPreset = (doc, payload) => {
    const merged = { ...payload, ...doc };
    rememberProfile(merged);
    setLoadedPreset({ profile_id: merged.profile_id, baseline: normalizePersistedProfile(payload) });
    setActivePresetId(null);
    setProfile((p) => ({ ...p, name: payload.name }));
  };

  const systemPresetName = () =>
    activePresetId ? BUILTIN_PRESETS.find((p) => p.id === activePresetId)?.name : null;

  const createPreset = async (name) => {
    const payload = { ...buildPayload(), name };
    const created = await createAutoSourcingProfile(payload);
    if (created?.profile_id) adoptSavedPreset(created, payload);
    return created;
  };

  const runPresetAction = async (key, fn) => {
    if (submittingRef.current) return null;
    submittingRef.current = true;
    setPendingAction(key);
    try {
      return await fn();
    } finally {
      submittingRef.current = false;
      setPendingAction(null);
    }
  };

  const saveChanges = () =>
    runPresetAction("save", async () => {
      if (!loadedPreset || !validateBeforeSubmit()) return null;
      const payload = buildPayload();
      try {
        const doc = await updateAutoSourcingProfile(loadedPreset.profile_id, payload);
        adoptSavedPreset({ ...(doc || {}), profile_id: loadedPreset.profile_id }, payload);
        toast.success("Saved");
        return doc;
      } catch (err) {
        toast.error(friendlyAutoSourcingError(err, { action: "save" }));
        return null;
      }
    });

  const saveAsNew = () =>
    runPresetAction("save", async () => {
      if (!profile.scope.source_ids.length) {
        toast.error("Select a retailer first.");
        return null;
      }
      if (!validateBeforeSubmit()) return null;
      const names = presets.map((p) => p.name);
      const typed = String(profile.name || "").trim();
      const base = loadedPresetDoc && typed === loadedPresetDoc.name ? `${typed} Copy` : null;
      const name = base
        ? uniquePresetName(base, names)
        : generatedPresetName({ typedName: typed, autoName: autoName(), systemPresetName: systemPresetName(), existingNames: names });
      try {
        const created = await createPreset(name);
        toast.success(`Saved as “${name}”`);
        void loadProfiles();
        return created;
      } catch (err) {
        toast.error(friendlyAutoSourcingError(err, { action: "save" }));
        return null;
      }
    });

  const discardChanges = () => {
    if (loadedPresetDoc) loadProfileIntoForm(loadedPresetDoc, { silent: true });
  };

  const renamePreset = async (preset, name) => {
    try {
      const doc = await updateAutoSourcingProfile(preset.profile_id, { name });
      const newName = doc?.name || name;
      setPresets((prev) => prev.map((p) => (p.profile_id === preset.profile_id ? { ...p, ...(doc || {}), name: newName } : p)));
      if (loadedPreset?.profile_id === preset.profile_id) {
        setLoadedPreset((lp) => (lp ? { ...lp, baseline: { ...lp.baseline, name: newName } } : lp));
        setProfile((p) => (String(p.name || "").trim() === preset.name ? { ...p, name: newName } : p));
      }
      toast.success("Preset renamed");
      return true;
    } catch (err) {
      toast.error(friendlyAutoSourcingError(err, { action: "save" }));
      return false;
    }
  };

  const toggleDefaultPreset = async (preset) => {
    const next = !preset.is_default;
    try {
      await updateAutoSourcingProfile(preset.profile_id, { is_default: next });
      setPresets((prev) =>
        prev.map((p) => ({
          ...p,
          is_default: p.profile_id === preset.profile_id ? next : next ? false : Boolean(p.is_default),
        }))
      );
      toast.success(next ? `“${preset.name}” is your default preset` : "Default preset removed");
    } catch (err) {
      toast.error(friendlyAutoSourcingError(err, { action: "save" }));
    }
  };

  const clonePreset = async (preset) => {
    try {
      const doc = await cloneAutoSourcingProfile(preset.profile_id);
      if (doc?.profile_id) {
        setPresets((prev) => [doc, ...prev.filter((p) => p.profile_id !== doc.profile_id)]);
        toast.success(`Cloned as “${doc.name}”`);
      }
    } catch (err) {
      toast.error(friendlyAutoSourcingError(err, { action: "save" }));
    }
  };

  const resetToBlank = () => {
    const blank = cloneProfile();
    setProfile(blank);
    setMode("basic");
    setCategoryUrlText("");
    setLoadedPreset(null);
    setActivePresetId("all");
    setSystemBaseline(systemPresetSignature(blank));
  };

  const deletePreset = async (preset) => {
    setPresetBusy(true);
    try {
      await deleteAutoSourcingProfile(preset.profile_id);
      setPresets((prev) => prev.filter((p) => p.profile_id !== preset.profile_id));
      if (loadedPreset?.profile_id === preset.profile_id) resetToBlank();
      toast.success("Preset deleted");
      return true;
    } catch (err) {
      toast.error(err?.message || "Couldn't delete preset.");
      return false;
    } finally {
      setPresetBusy(false);
    }
  };

  const validateBeforeSubmit = () => {
    if (categorySelection?.mode === "custom" && !String(categorySelection.user_query || "").trim()) {
      toast.error(CUSTOM_QUERY_REQUIRED_ERROR);
      return false;
    }
    if (profile.schedule.mode === "recurring" && !profile.schedule.interval_minutes) {
      toast.error("Recurring schedules need a repeat interval.");
      return false;
    }
    if (
      durationInvalid ||
      Number(profile.scan_limits.max_duration_minutes) > MAX_SCAN_DURATION_MINUTES
    ) {
      toast.error(
        "Maximum continuous scan duration is 72 hours. You can resume the scan afterward.",
      );
      return false;
    }
    // Numeric scan limits must be caught here with a specific message. The
    // server rejects out-of-range values with a validation error, and the
    // customer previously saw only "check your search settings" with no clue
    // which field was wrong.
    if (
      profile.scan_limits.products_per_source_mode !== "all" &&
      Number(profile.scan_limits.max_products_per_source) < 1
    ) {
      toast.error("Maximum Products to Discover must be at least 1. Use All Available Products for no limit.");
      return false;
    }
    if (Number(profile.scan_limits.max_sources) < 1) {
      toast.error("Max sources must be at least 1.");
      return false;
    }
    const budget = Number(profile.scan_limits.max_provider_requests);
    if (!Number.isFinite(budget) || budget < 0) {
      toast.error("Provider budget must be 0 or more. 0 means direct discovery only.");
      return false;
    }
    return true;
  };

  // Save Profile: create the profile only. 0 provider calls, no run.
  const saveProfile = async () => {
    if (submittingRef.current) return null;
    if (!profile.scope.source_ids.length) {
      toast.error("Select a retailer first.");
      return null;
    }
    if (!validateBeforeSubmit()) return null;
    if (loadedPreset) {
      return presetDirty ? saveChanges() : null;
    }
    submittingRef.current = true;
    setPendingAction("save");
    try {
      const name = generatedPresetName({
        typedName: profile.name,
        autoName: autoName(),
        systemPresetName: systemPresetName(),
        existingNames: presets.map((p) => p.name),
      });
      const created = await createPreset(name);
      toast.success("Search saved to My presets.");
      void loadProfiles();
      return created;
    } catch (err) {
      toast.error(friendlyAutoSourcingError(err, { action: "save" }));
      return null;
    } finally {
      submittingRef.current = false;
      setPendingAction(null);
    }
  };

  // Start Search: create the profile, then POST /profiles/{id}/run (which
  // creates the run and enqueues the worker job). One click = one run.
  const startSearch = async () => {
    if (submittingRef.current) return;
    if (!selectedOperational) {
      toast.error("Select a retailer first.");
      return;
    }
    if (!validateBeforeSubmit()) return;
    if (!categoryReady) {
      toast.error(
        categoryState.kind === "manual"
          ? "Enter 1 to 25 category URLs from this retailer."
          : categoryState.kind === "manual_optional_urls"
            ? "Choose a keyword search (All or Custom) or paste optional category URLs."
          : categoryState.kind === "pending"
            ? "Category discovery for this retailer is still being verified."
            : "Choose a category first."
      );
      return;
    }
    submittingRef.current = true;
    setPendingAction("start");
    try {
      let profileId = loadedPreset && !presetDirty ? loadedPreset.profile_id : null;
      if (!profileId) {
        const created = await createAutoSourcingProfile(buildPayload());
        rememberProfile(created);
        profileId = created.profile_id;
      }
      const run = await runAutoSourcingProfile(profileId);
      setStatus({ key: run?.status || "queued", label: _statusLabel(run?.status || "queued") });
      setActiveRunId(run?.run_id || null);
      if (run?.run_id) {
        // Bind the job id at submit time so a navigation cannot lose it.
        writeScanState({ activeRunId: run.run_id, selectedRunId: run.run_id, tab: "results" });
      }
      if (run?.run_id) {
        setSelectedRun(run);
        setRuns((prev) => [run, ...prev.filter((r) => r.run_id !== run.run_id)]);
      }
      toast.success("Search started. Watching live results…");
      setTab("results");
      void loadRuns();
      void loadProfiles();
      return run;
    } catch (err) {
      toast.error(friendlyAutoSourcingError(err, { action: "start" }));
      void loadProfiles();
    } finally {
      submittingRef.current = false;
      setPendingAction(null);
    }
  };

  // Restore a saved profile into the full form (retailer, category, economics,
  // demand, competition, adjustments, match safety, scan limits, schedule).
  const loadProfileIntoForm = (saved, { silent = false } = {}) => {
    const next = cloneProfile();
    for (const section of [
      "name", "mode", "scope", "match_strategy", "adjustments", "exclusions",
      "title_rules", "costs", "profitability", "demand", "competition",
      "availability", "scan_limits", "schedule",
    ]) {
      if (saved[section] !== undefined) next[section] = JSON.parse(JSON.stringify(saved[section]));
    }
    next.scope = { ...next.scope, category_selection: restoreCategorySelection(saved.scope) };
    const savedMode = saved.mode || "basic";
    const urls = Array.isArray(saved.scope?.category_urls) ? saved.scope.category_urls : [];
    const source = sources.find((s) => s.source_id === next.scope.source_ids?.[0]);
    setProfile(next);
    setMode(savedMode);
    setCategoryUrlText(urls.join("\n"));
    setActivePresetId(null);
    setLoadedPreset({
      profile_id: saved.profile_id,
      baseline: normalizePersistedProfile(
        serializeAutoSourcingProfile(next, { mode: savedMode, source, categoryUrls: urls })
      ),
    });
    if (!silent) toast.success(`Loaded “${saved.name || "profile"}”`);
  };

  // Page open: load the user's default preset once (never over user edits).
  const initialProfileRef = useRef(stableStringify(cloneProfile()));
  useEffect(() => {
    if (!profilesLoaded || defaultHandledRef.current) return;
    defaultHandledRef.current = true;
    const def = presets.find((p) => p.is_default);
    if (def && !loadedPreset && stableStringify(profile) === initialProfileRef.current) {
      loadProfileIntoForm(def, { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilesLoaded, presets]);

  const presetActions = {
    onLoad: (p) => loadProfileIntoForm(p),
    onRename: renamePreset,
    onToggleDefault: toggleDefaultPreset,
    onClone: clonePreset,
    onSaveChanges: () => void saveChanges(),
    onSaveAsNew: () => void saveAsNew(),
    onDiscard: discardChanges,
    onRequestDelete: (p) => setPendingDelete(p),
  };

  // The scan the customer is following, and whether another one is also live.
  const activeScans = activeRunChoices(runs, activeRunId);
  const focusedScan = activeScans.focused;
  const watchingFocused =
    focusedScan?.run_id && selectedRun?.run_id
      ? String(focusedScan.run_id) === String(selectedRun.run_id)
      : false;
  const showActiveScanBanner =
    Boolean(focusedScan?.run_id) && (tab !== "results" || !watchingFocused);

  const openRunResults = (run) => {
    if (!run?.run_id) return;
    setSelectedRun(run);
    setActiveRunId(run.run_id);
    writeScanState({ activeRunId: run.run_id, selectedRunId: run.run_id, tab: "results" });
    setTab("results");
  };

  const pageTitle = "Auto Sourcing";
  const pageSubtitle =
    "Search approved retailers and uncover profitable products to sell on Walmart.";

  return (
    <div className="auto-sourcing-page" data-testid="auto-sourcing">
      <header className="auto-sourcing-header">
        <div className="as-header-text">
          <h1>{pageTitle}</h1>
          <p>{pageSubtitle}</p>
        </div>
        <div className="as-header-right">
          <span className={`as-status ${_statusClass(status.key)}`} data-testid="auto-sourcing-status">
            {status.label}
          </span>
        </div>
      </header>

      <div className="as-tabs" role="tablist" aria-label="Auto Sourcing tabs">
        {[
          ["search", "Search"],
          ["results", "Results"],
          ["presets", "Saved Profiles"],
          ["history", "History"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`as-tab ${tab === id ? "active" : ""}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {showActiveScanBanner ? (
        <ActiveScanBanner
          run={focusedScan}
          sources={sources}
          onViewResults={openRunResults}
        />
      ) : null}

      {tab === "search" && (
        <div className="as-workspace">
          <div className="as-panel">
            <div className="as-panel-header">
              <div>
                <div className="as-section-title">Source & Target</div>
                <div className="as-section-description">
                  {mode === "basic"
                    ? "Tell Ezisell what you're looking for. We'll handle the sourcing details."
                    : "Fine-tune source selection, matching, costs, profitability, demand, and scan limits."}
                </div>
              </div>
              <div className="as-tabs" style={{ marginTop: 0 }}>
                {["basic", "advanced"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`as-tab ${mode === m ? "active" : ""}`}
                    onClick={() => {
                      setMode(m);
                      // Persist mode so a search configured in Advanced stays advanced.
                      setProfile((p) => ({ ...p, mode: m }));
                    }}
                  >
                    {m[0].toUpperCase() + m.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="as-section">
              <div className="as-section-title">Retailers</div>
              <div className="as-section-description">
                Choose one or more Ready retailers for this search. Coming soon
                retailers stay visible but cannot be selected.
              </div>
              <RetailerPicker
                sources={sources}
                multiple
                value={profile.scope.source_ids}
                onChange={changeRetailers}
                loading={sourcesLoading}
                error={sourcesError}
                refreshFailed={directory.refreshFailed}
                notice={readyNotice}
                onRetry={() => void loadSources()}
                aria-label="Select retailers"
              />
              {sourcesError && (
                <div className="as-inline-error" data-testid="auto-sourcing-sources-error">
                  <span>{sourcesError}</span>
                  <button type="button" className="as-retry" onClick={() => void loadSources()}>
                    Retry
                  </button>
                </div>
              )}
            </div>

            {mode === "basic" ? (
              <>
                <div className="as-section">
                  <div className="as-section-title">Quick Setup</div>
                  <div className="as-grid-2" style={{ marginBottom: 14 }}>
                    <Field label="Search name" helper="Optional — we'll name it for you.">
                      <input
                        className="as-input"
                        value={profile.name}
                        onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                        placeholder={autoName() || "e.g. Office Supplies — 30 ROI"}
                      />
                    </Field>
                    {multiRetailer ? (
                      <div className="as-helper" data-testid="multi-retailer-category-hint">
                        Configure each retailer below — categories and keywords stay independent.
                      </div>
                    ) : categoryState.kind === "manual" ? (
                      <Field label="Category URL(s)">
                        <ManualCategoryUrls
                          value={categoryUrlText}
                          onChange={changeManualUrls}
                          domain={selectedSource?.domain}
                          saved={savedManualUrls}
                        />
                      </Field>
                    ) : categoryState.kind === "pending" ? (
                      <div className="as-field" data-testid="category-pending">
                        <label>Category</label>
                        <div className="as-pending-state" role="status">
                          <strong>Verification Pending</strong>
                          <span>We're still checking how to browse this retailer's categories. You'll be able to search it once that's done.</span>
                        </div>
                      </div>
                    ) : (
                    <Field label="Category" helper={categoryState.helper}>
                      <CategoryPicker
                        categories={
                          categoryState.kind === "category_catalog" ||
                          (categoryState.kind === "manual_optional_urls" &&
                            categoryState.searchKind === "category_catalog")
                            ? sourceCategories
                            : []
                        }
                        loading={
                          (categoryState.kind === "category_catalog" ||
                            (categoryState.kind === "manual_optional_urls" &&
                              categoryState.searchKind === "category_catalog")) &&
                          categoriesLoading
                        }
                        value={categoryState.disabled || categorySelection?.mode === "urls" ? null : categorySelection}
                        onChange={changeCategorySelection}
                        onCustomQueryChange={setCustomQueryValue}
                        disabled={categoryState.disabled}
                        inactiveSelection={selectedCategoryInactive}
                      />
                    </Field>
                    )}
                  </div>
                  {multiRetailer ? (
                    <RetailerScopePanels
                      sourceIds={profile.scope.source_ids}
                      sources={sources}
                      sourceScope={profile.scope.source_scope || {}}
                      onChangeSlice={changeRetailerSlice}
                    />
                  ) : null}
                  {categoryState.kind === "manual_optional_urls" && (
                    <Field
                      label="Category URL(s)"
                      helper="Optional — paste category pages if you have them. Not required for keyword search."
                    >
                      <ManualCategoryUrls
                        value={categoryUrlText}
                        onChange={changeManualUrls}
                        domain={selectedSource?.domain}
                        saved={savedManualUrls}
                        optional
                      />
                    </Field>
                  )}
                  <Field label="Presets">
                    <PresetBar
                      systemPresets={BUILTIN_PRESETS}
                      activeSystemId={activePresetId}
                      systemModified={systemModified}
                      onApplySystem={applyPreset}
                      presets={sortedPresets}
                      activePresetId={loadedPreset?.profile_id || null}
                      dirty={presetDirty}
                      actions={presetActions}
                      onSaveCurrent={() => void (loadedPreset ? saveAsNew() : saveProfile())}
                    />
                  </Field>
                </div>

                <div className="as-section">
                  <div className="as-section-title">Profit Targets</div>
                  <div className="as-section-description">
                    {sectionEnabled("profitability")
                      ? "Filter results by profitability (estimated — marketplace fees excluded)."
                      : "Profitability filters are not applied to sourcing yet."}
                  </div>
                  <div className="as-grid-3">
                    <NumberField
                      label="Minimum ROI %"
                      value={profile.profitability.min_estimated_roi}
                      onChange={(v) => setProfit({ min_estimated_roi: v })}
                      placeholder="Any"
                      min={0}
                      disabled={!fieldEnabled("profitability.min_estimated_roi")}
                    />
                    <NumberField
                      label="Minimum Profit $"
                      value={profile.profitability.min_estimated_profit}
                      onChange={(v) => setProfit({ min_estimated_profit: v })}
                      placeholder="Any"
                      min={0}
                      disabled={!fieldEnabled("profitability.min_estimated_profit")}
                    />
                    <NumberField
                      label="Max Source Cost $"
                      value={profile.profitability.max_acquisition_cost}
                      onChange={(v) => setProfit({ max_acquisition_cost: v })}
                      placeholder="No limit"
                      min={0}
                      disabled={!fieldEnabled("profitability.max_acquisition_cost")}
                    />
                    <NumberField
                      label="Minimum Margin %"
                      value={profile.profitability.min_margin}
                      onChange={(v) => setProfit({ min_margin: v })}
                      placeholder="Any"
                      min={0}
                      disabled={!fieldEnabled("profitability.min_margin")}
                    />
                  </div>
                </div>

                <div className="as-section">
                  <div className="as-section-title">Walmart Demand</div>
                  <div className="as-section-description">
                    Rating and review filters are enforced from real persisted Walmart data.
                    Monthly sold is not available yet.
                  </div>
                  <div className="as-grid-3">
                    <NumberField
                      label="Min Monthly Sold"
                      value={profile.demand.min_monthly_sold}
                      onChange={(v) => setDemand({ min_monthly_sold: v })}
                      min={0}
                      disabled={fieldUnavailable("demand.min_monthly_sold")}
                    />
                    <NumberField
                      label="Min Rating"
                      value={profile.demand.min_rating}
                      onChange={(v) => setDemand({ min_rating: v })}
                      min={0}
                      max={5}
                      step={0.1}
                      disabled={!fieldEnabled("demand.min_rating")}
                    />
                    <NumberField
                      label="Min Reviews"
                      value={profile.demand.min_reviews}
                      onChange={(v) => setDemand({ min_reviews: v })}
                      min={0}
                      disabled={!fieldEnabled("demand.min_reviews")}
                    />
                    <NumberField
                      label="Max Sellers"
                      value={profile.competition.max_seller_count}
                      onChange={(v) => setCompetition({ max_seller_count: v })}
                      min={0}
                      disabled={!fieldEnabled("competition.max_seller_count")}
                    />
                  </div>
                </div>

                <div className="as-section">
                  <div className="as-section-title">Store Adjustments</div>
                  <div className="as-section-description">
                    {sectionEnabled("adjustments")
                      ? "Adjust the acquisition price for this search."
                      : "Price adjustments will become available when Auto Sourcing economics are enabled."}
                  </div>
                  <div className="as-grid-3">
                    <NumberField
                      label="Store Discount %"
                      value={profile.adjustments.store_discount_percent}
                      onChange={(v) => setAdjust({ store_discount_percent: v ?? 0 })}
                      min={0}
                      max={100}
                      disabled={!sectionEnabled("adjustments")}
                    />
                    <NumberField
                      label="Cashback %"
                      value={profile.adjustments.cashback_percent}
                      onChange={(v) => setAdjust({ cashback_percent: v ?? 0 })}
                      min={0}
                      max={100}
                      disabled={!sectionEnabled("adjustments")}
                    />
                    <NumberField
                      label="Gift Card %"
                      value={profile.adjustments.gift_card_percent}
                      onChange={(v) => setAdjust({ gift_card_percent: v ?? 0 })}
                      min={0}
                      max={100}
                      disabled={!sectionEnabled("adjustments")}
                    />
                  </div>
                </div>

                <div className="as-section">
                  <div className="as-section-title">Match Safety</div>
                  <div className="as-section-description">
                    {sectionEnabled("exclusions")
                      ? "Control which candidates qualify for this search."
                      : "Match strategy overrides are not applied to sourcing yet. Canonical hard identity safety always applies."}
                  </div>
                  <div className="as-grid-2">
                    {[
                      ["verified", "Verified matches only", "require_identifier", "match_strategy"],
                      ["pack", "Exclude pack mismatches", "pack_mismatch", "exclusions"],
                      ["src_stock", "Source must be in stock", "source_oos", "exclusions"],
                      ["wmt_stock", "Walmart must be available", "walmart_oos", "exclusions"],
                    ].map(([key, label, field, section]) => {
                      const checked = profile.match_strategy[field] ?? profile.exclusions[field];
                      const toggle =
                        field === "require_identifier" ? setMatch : setExclusions;
                      const enabled = sectionEnabled(section);
                      return (
                        <label key={key} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input
                            type="checkbox"
                            checked={Boolean(checked)}
                            disabled={!enabled}
                            onChange={(e) => toggle({ [field]: e.target.checked })}
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="as-section">
                <div className="as-section-title">Search Profile</div>
                <div className="as-grid-2" style={{ marginBottom: 14 }}>
                  <Field label="Sourcing Profile Name">
                    <input
                      className="as-input"
                      value={profile.name}
                      onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Office Supplies — 30 ROI"
                    />
                  </Field>
                  <Field
                    label="Schedule"
                    helper="Recurring configuration is saved here. Production recurring execution is activated only through the authorized workspace/operator workflow."
                  >
                    <GlassCombobox
                      testId="schedule-combobox"
                      searchable={false}
                      label={profile.schedule.mode === "recurring" ? "Recurring" : "Run once"}
                      listLabel="Schedule"
                      maxHeight={200}
                      options={[
                        ["once", "Run once"],
                        ["recurring", "Recurring"],
                      ].map(([value, text]) => ({
                        key: value,
                        label: text,
                        selected: profile.schedule.mode === value,
                        onSelect: () => setSchedule({ mode: value }),
                      }))}
                    />
                  </Field>
                  {profile.schedule.mode === "recurring" && (
                    <Field
                      label="Repeat every (minutes)"
                      helper="Minimum supported cadence is 60 minutes."
                    >
                      <input
                        className="as-input"
                        type="number"
                        min={60}
                        step={1}
                        value={profile.schedule.interval_minutes ?? ""}
                        placeholder="e.g. 60"
                        onChange={(e) =>
                          setSchedule({
                            interval_minutes: e.target.value === "" ? null : Number(e.target.value),
                          })
                        }
                      />
                    </Field>
                  )}
                </div>

                {multiRetailer ? (
                  <RetailerScopePanels
                    sourceIds={profile.scope.source_ids}
                    sources={sources}
                    sourceScope={profile.scope.source_scope || {}}
                    onChangeSlice={changeRetailerSlice}
                  />
                ) : (
                  <>
                    {categoryState.kind === "manual_optional_urls" && (
                      <Field label="Category" helper={categoryState.helper}>
                        <CategoryPicker
                          categories={
                            categoryState.searchKind === "category_catalog" ? sourceCategories : []
                          }
                          loading={categoryState.searchKind === "category_catalog" && categoriesLoading}
                          value={categorySelection?.mode === "urls" ? null : categorySelection}
                          onChange={changeCategorySelection}
                          onCustomQueryChange={setCustomQueryValue}
                          inactiveSelection={selectedCategoryInactive}
                        />
                      </Field>
                    )}
                    {discoveryMode === "manual" ? (
                      <Field
                        label="Category URL(s)"
                        helper={
                          categoryState.kind === "manual_optional_urls"
                            ? "Optional — paste category pages if you have them. Not required for keyword search."
                            : undefined
                        }
                      >
                        <ManualCategoryUrls
                          value={categoryUrlText}
                          onChange={changeManualUrls}
                          domain={selectedSource?.domain}
                          saved={savedManualUrls}
                          optional={categoryState.kind === "manual_optional_urls"}
                        />
                      </Field>
                    ) : discoveryMode ? null : (
                    <Field
                      label="Category URLs"
                      helper={
                        selectedSource?.category_support?.supported
                          ? "Add one category or collection URL per line. URLs must belong to the selected retailer."
                          : "Category browsing isn't available for this retailer yet."
                      }
                    >
                      <textarea
                        className="as-textarea"
                        value={categoryUrlText}
                        disabled={!selectedSource?.category_support?.supported}
                        onChange={(e) => setCategoryUrlText(e.target.value)}
                        placeholder={"https://www.officesupply.com/cleaning\nhttps://www.officesupply.com/office"}
                      />
                    </Field>
                    )}
                  </>
                )}

                <div className="as-grid-2" style={{ marginTop: 14 }}>
                  <Field
                    label="Data Freshness (hours)"
                    helper="Not applied to sourcing yet."
                  >
                    <input
                      className="as-input"
                      type="number"
                      min={0}
                      max={120}
                      disabled
                      value={profile.scope.freshness_hours}
                      onChange={(e) => setScope({ freshness_hours: Number(e.target.value) || 0 })}
                    />
                  </Field>
                </div>
              </div>
            )}

            <div className="as-section">
              <div className="as-section-title">Scan Limits</div>
              <div className="as-grid-3">
                <Field
                  label="Products per Source"
                  helper={
                    profile.scan_limits.products_per_source_mode === "all"
                      ? "Collects every eligible genuine product until categories, budget, or duration end. Not unlimited paid requests."
                      : "Stop after this many genuine products per retailer."
                  }
                >
                  <select
                    className="as-input"
                    data-testid="as-products-per-source-mode"
                    value={profile.scan_limits.products_per_source_mode || "custom"}
                    onChange={(e) =>
                      setLimits({
                        products_per_source_mode: e.target.value === "all" ? "all" : "custom",
                      })
                    }
                  >
                    <option value="all">All Available Products</option>
                    <option value="custom">Custom Maximum</option>
                  </select>
                </Field>
                {profile.scan_limits.products_per_source_mode === "all" ? (
                  <div className="as-helper" data-testid="as-products-all-available" style={{ alignSelf: "end", paddingBottom: 8 }}>
                    No flat product ceiling — discovery continues while work remains.
                  </div>
                ) : (
                  <NumberField
                    label="Maximum Products to Discover"
                    value={profile.scan_limits.max_products_per_source}
                    onChange={(v) => setLimits({ max_products_per_source: v ?? 40 })}
                    min={1}
                  />
                )}
                <NumberField
                  label="Max Sources"
                  value={profile.scan_limits.max_sources}
                  onChange={(v) => setLimits({ max_sources: v ?? 1 })}
                  min={1}
                />
                <NumberField
                  label="Provider budget"
                  value={profile.scan_limits.max_provider_requests}
                  onChange={(v) => {
                    if (v == null || Number.isNaN(v)) {
                      setLimits({ max_provider_requests: 60 });
                      return;
                    }
                    // Never silently raise 0 — zero means direct discovery only.
                    setLimits({ max_provider_requests: Math.max(0, Math.floor(v)) });
                  }}
                  min={0}
                  helper="0 = direct discovery only (no paid HTML calls). The server never raises your limit; values above the operator ceiling are capped down."
                />
              </div>
              <div className="as-grid-2" style={{ marginTop: 14 }}>
                <ScanDurationField
                  minutes={profile.scan_limits.max_duration_minutes}
                  onMinutesChange={(mins) => setLimits({ max_duration_minutes: mins })}
                  onInvalidChange={setDurationInvalid}
                />
              </div>
              <div
                className="as-scan-limits-summary"
                data-testid="as-scan-limits-summary"
              >
                {(() => {
                  const parts = minutesToDurationParts(
                    profile.scan_limits.max_duration_minutes,
                  );
                  // Compound adjective uses the singular: "12-hour scan", "30-minute scan".
                  const unitLabel =
                    parts.unit === "days"
                      ? "day"
                      : parts.unit === "hours"
                        ? "hour"
                        : "minute";
                  const budget = Number(profile.scan_limits.max_provider_requests);
                  const productsLabel =
                    profile.scan_limits.products_per_source_mode === "all"
                      ? "Discover all available products per source"
                      : `Discover up to ${profile.scan_limits.max_products_per_source} products per source`;
                  return `${productsLabel} · ${profile.scan_limits.max_sources} source${Number(profile.scan_limits.max_sources) === 1 ? "" : "s"} · ${parts.amount}-${unitLabel} scan · ${budget === 0 ? "no paid requests" : `${budget} paid request${budget === 1 ? "" : "s"} maximum`}`;
                })()}
              </div>
              <div
                className="as-helper"
                data-testid="as-provider-budget-preview"
                style={{ marginTop: 8 }}
              >
                {Number(profile.scan_limits.max_provider_requests) === 0
                  ? "Effective paid-provider budget for this search: 0 (direct / Shopify discovery only)."
                  : `Effective paid-provider budget for this search: ${profile.scan_limits.max_provider_requests} request${
                      Number(profile.scan_limits.max_provider_requests) === 1 ? "" : "s"
                    } (server may only lower this ceiling).`}
              </div>
            </div>

            {mode === "advanced" &&
              ACCORDIONS.map(({ key, label }) => (
                <div className="as-accordion" key={key}>
                  <button
                    type="button"
                    className="as-accordion-button"
                    aria-expanded={Boolean(profile._open?.[key])}
                    onClick={() => toggleAccordion(key)}
                  >
                    {label}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  {profile._open?.[key] && (
                    <div className="as-accordion-body">
                      {!sectionConfigured(key) && (
                        <div className="as-helper" style={{ marginBottom: 10 }}>
                          Not applied to sourcing yet — canonical hard identity safety
                          still applies.
                        </div>
                      )}
                      {capabilities[key] === "partial" && (
                        <div className="as-helper" style={{ marginBottom: 10 }}>
                          Partially applied — some fields are enforced, others remain
                          unavailable.
                        </div>
                      )}
                      <AccordionBody
                        section={key}
                        profile={profile}
                        disabled={!sectionConfigured(key)}
                        fieldEnabled={fieldEnabled}
                        setters={{
                          match_strategy: setMatch,
                          adjustments: setAdjust,
                          exclusions: setExclusions,
                          title_rules: setTitleRules,
                          costs: setCosts,
                          profitability: setProfit,
                          demand: setDemand,
                          competition: setCompetition,
                          availability: setAvailability,
                          scan_limits: setLimits,
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}

            <div className="as-sticky-actions">
              <button
                type="button"
                data-testid="auto-sourcing-save"
                className={`as-cta as-cta-secondary ${pendingAction === "save" ? "is-loading" : ""}`}
                disabled={saving || (Boolean(loadedPreset) && !presetDirty)}
                aria-busy={pendingAction === "save"}
                onClick={saveProfile}
              >
                {pendingAction === "save" ? (
                  <><Loader2 className="w-4 h-4 as-spin" aria-hidden="true" /> Saving...</>
                ) : loadedPreset && presetDirty ? (
                  <><Save className="w-4 h-4" aria-hidden="true" /> Save Changes</>
                ) : loadedPreset ? (
                  <><Check className="w-4 h-4" aria-hidden="true" /> Saved</>
                ) : (
                  <><Save className="w-4 h-4" aria-hidden="true" /> Save Profile</>
                )}
              </button>
              <button
                type="button"
                className={`as-cta ${pendingAction === "start" ? "is-loading" : ""}`}
                data-testid="auto-sourcing-start"
                disabled={
                  !selectedOperational || !categoryReady || saving || durationInvalid
                }
                title={
                  durationInvalid
                    ? "Maximum continuous scan duration is 72 hours. You can resume the scan afterward."
                    : undefined
                }
                aria-busy={pendingAction === "start"}
                onClick={startSearch}
              >
                {pendingAction === "start" ? (
                  <><Loader2 className="w-4 h-4 as-spin" aria-hidden="true" /> Starting...</>
                ) : (
                  <>
                    <Play className="w-4 h-4" aria-hidden="true" />
                    {profile.schedule.mode === "recurring" ? "Start Auto Sourcing" : "Start Search"}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="as-panel">
            <div className="as-panel-header">
              <div className="as-section-title">Source Guide</div>
            </div>
            <div className="as-section">
              {selectedSource ? (
                <>
                  <div className="as-source-guide-head">
                    <RetailerLogo source={selectedSource} size="lg" />
                    <div>
                      <div className="as-section-title">{selectedSource.name}</div>
                      <div className="as-section-description" style={{ marginBottom: 0 }}>{selectedSource.domain}</div>
                    </div>
                  </div>
                  <div className="as-grid-2">
                    <div>
                      <div className="as-helper">Match capabilities</div>
                      <div>
                        {selectedSource.match_capabilities?.length
                          ? selectedSource.match_capabilities.map((c) => (
                              <span key={c} className="as-source-status operational" style={{ marginRight: 6 }}>
                                {c.toUpperCase()}
                              </span>
                            ))
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="as-helper">Discovery</div>
                      <span className="as-source-status operational">
                        {selectedSource.category_support?.mode === "search_query"
                          ? "Custom Search"
                          : selectedSource.category_support?.category_count != null
                            ? `${selectedSource.category_support.category_count} verified categories`
                            : selectedSource.category_support?.mode === "category_catalog"
                              ? "Verified category browsing"
                              : "—"}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="as-helper">Select a retailer to see its source guide.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "results" && (
        // Stage L acceptance: no duplicate "Results" heading and no panel
        // chrome/margin — the compact dashboard is the first thing on the page.
        <div className="as-results-flush" data-testid="as-results-flush">
          {runs.length === 0 && !selectedRun ? (
            <div className="as-helper" data-testid="auto-sourcing-results-empty">
              No Auto Sourcing results yet.
            </div>
          ) : (
              <AutoSourcingResults
                run={selectedRun}
                sources={sources}
                opportunities={results}
                error={resultsError}
                onRefresh={() => {
                  if (selectedRun?.run_id) {
                    void getAutoSourcingRun(selectedRun.run_id).then((run) => {
                      if (run?.run_id) {
                        setSelectedRun(run);
                        setRuns((prev) =>
                          prev.map((r) => (r.run_id === run.run_id ? { ...r, ...run } : r)),
                        );
                      }
                    });
                    void loadResults({ run_id: selectedRun.run_id });
                  }
                }}
                onResume={() => {
                  if (!selectedRun?.run_id || !selectedRun?.resume_available) return;
                  void resumeAutoSourcingRun(selectedRun.run_id)
                    .then((run) => {
                      if (run?.run_id) {
                        setSelectedRun(run);
                        setActiveRunId(run.run_id);
                        setRuns((prev) =>
                          prev.map((r) => (r.run_id === run.run_id ? { ...r, ...run } : r)),
                        );
                        toast.message("Scan resumed from the last checkpoint.");
                      }
                    })
                    .catch(() => {
                      toast.message("Unable to resume this scan.");
                    });
                }}
                onViewHistory={() => setTab("history")}
                onPause={() => {
                  if (!selectedRun?.run_id) return;
                  void pauseAutoSourcingRun(selectedRun.run_id)
                    .then(() => {
                      toast.message(
                        "Pause requested — in-flight requests finish, then the clock freezes.",
                      );
                    })
                    .catch(() => {
                      toast.message("Unable to pause this scan.");
                    });
                }}
                onStop={() => {
                  if (!selectedRun?.run_id) return;
                  void stopAutoSourcingRun(selectedRun.run_id)
                    .then((run) => {
                      if (run?.run_id) {
                        setSelectedRun(run);
                        setRuns((prev) =>
                          prev.map((r) => (r.run_id === run.run_id ? { ...r, ...run } : r)),
                        );
                      }
                      setActiveRunId(null);
                      toast.message("Scan stopped.");
                    })
                    .catch(() => {
                      toast.message("Unable to stop this scan.");
                    });
                }}
                onAnalyze={(lead) => {
                  const key = lead?.walmartItemId || lead?.upc || lead?.gtin;
                  if (key) nav(analyzerHref(key));
                }}
                onSave={() => {
                  toast.message("Open Product Analyzer to save matched products to Saved Leads.");
                }}
                onOpen={(lead) => {
                  const key = lead?.walmartItemId || lead?.upc || lead?.gtin;
                  if (key) nav(analyzerHref(key));
                }}
              />
            )}
        </div>
      )}

      {tab === "presets" && (
        <div className="as-panel" style={{ marginTop: 18 }}>
          <div className="as-panel-header">
            <div className="as-section-title">Presets</div>
          </div>
          <div className="as-section">
            <div className="as-grid-2" style={{ marginBottom: 18 }}>
              {BUILTIN_PRESETS.map((p) => (
                <div key={p.id} className="as-panel" style={{ padding: 14 }}>
                  <div className="as-section-title">{p.name}</div>
                  <div className="as-helper">{p.description}</div>
                  <button type="button" className="as-cta" onClick={() => applyPreset(p)}>
                    <Wand2 className="w-4 h-4" aria-hidden="true" /> Apply
                  </button>
                </div>
              ))}
            </div>
            <div className="as-section-title" style={{ marginTop: 14 }}>
              Your saved profiles
            </div>
            {profilesError ? (
              <div className="as-inline-error" data-testid="auto-sourcing-profiles-error">
                <span>{profilesError}</span>
                <button type="button" className="as-retry" onClick={() => void loadProfiles()}>
                  Retry
                </button>
              </div>
            ) : presets.length === 0 ? (
              <div className="as-helper" data-testid="auto-sourcing-profiles-empty">
                No saved profiles yet.
              </div>
            ) : (
              presets.map((p) => (
                <div
                  key={p.profile_id}
                  className="as-panel"
                  style={{ padding: "12px 14px", marginBottom: 10, display: "flex", alignItems: "center" }}
                >
                  <div style={{ flex: 1 }}>
                    <strong>{p.name}</strong>
                    <div className="as-helper">{p.mode} · {p.scope?.source_ids?.join(", ") || "no source"}</div>
                  </div>
                  <button
                    type="button"
                    className="as-cta as-cta-secondary"
                    aria-label={`Delete ${p.name}`}
                    onClick={() => setPendingDelete(p)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {pendingDelete ? (
        <ConfirmDelete
          preset={pendingDelete}
          busy={presetBusy}
          onCancel={() => setPendingDelete(null)}
          onConfirm={async () => {
            if (await deletePreset(pendingDelete)) setPendingDelete(null);
          }}
        />
      ) : null}

      {tab === "history" && (
        <div className="as-panel" style={{ marginTop: 18 }}>
          <div className="as-panel-header">
            <div className="as-section-title">History</div>
            <button type="button" className="as-cta as-cta-secondary" onClick={() => void loadRuns()}>
              <RotateCw className="w-4 h-4" />
            </button>
          </div>
          <div className="as-section">
            {runsError ? (
              <div className="as-inline-error" data-testid="auto-sourcing-runs-error">
                <span>{runsError}</span>
                <button type="button" className="as-retry" onClick={() => void loadRuns()}>
                  Retry
                </button>
              </div>
            ) : runs.length === 0 ? (
              <div className="as-helper" data-testid="auto-sourcing-history-empty">No runs yet.</div>
            ) : (
              <RunList
                runs={runs}
                sources={sources}
                onViewResults={(run) => {
                  setSelectedRun(run);
                  setTab("results");
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function runTitle(run, sources) {
  const name = run.profile_snapshot?.name;
  if (name) return name;
  const names = (run.source_ids || []).map(
    (id) => sources.find((s) => s.source_id === id)?.name || id
  );
  return names.join(", ") || "—";
}

function RunList({ runs, sources = [], onViewResults }) {
  return (
    <div>
      {runs.map((r) => (
        <div
          key={r.run_id}
          className="as-panel"
          style={{ padding: "12px 14px", marginBottom: 10 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <strong style={{ flex: 1 }} data-testid="auto-sourcing-run-title">{runTitle(r, sources)}</strong>
            <span className={`as-source-status ${runStatusClass(r.status)}`}>
              {_statusLabel(r.status || "queued")}
            </span>
            {onViewResults && (
              <button
                type="button"
                className="as-cta as-cta-secondary"
                onClick={() => onViewResults(r)}
              >
                View Results
              </button>
            )}
          </div>
          <div className="as-helper">
            Collected {r.progress?.products_collected ?? 0} · Matched{" "}
            {r.progress?.matched ?? 0} · Review {r.progress?.review ?? 0} · Rejected{" "}
            {r.progress?.rejected ?? 0}
            {typeof r.progress?.provider_calls === "number"
              ? ` · Provider ${r.progress.provider_calls}/${r.progress.provider_call_limit ?? "—"}`
              : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

function AccordionBody({ section, profile, setters, disabled, fieldEnabled }) {
  const set = setters[section];
  const v = profile[section];
  if (!set || !v) return null;

  // Per-field enabled: honor the field-level capability map when provided;
  // otherwise fall back to the section-level `disabled` flag.
  const isFieldDisabled = (key) => {
    if (fieldEnabled) {
      const cap = fieldEnabled(`${section}.${key}`);
      return disabled || !cap;
    }
    return disabled;
  };

  // Render a generic, honest field editor for each boolean/number/list value.
  const entries = Object.entries(v).filter(([k]) => k !== "_open");
  return (
    <div className="as-grid-2">
      {entries.map(([key, val]) => {
        const fDisabled = isFieldDisabled(key);
        if (typeof val === "boolean") {
          return (
            <label key={key} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={val}
                disabled={fDisabled}
                onChange={(e) => set({ [key]: e.target.checked })}
              />
              <span>{key.replace(/_/g, " ")}</span>
            </label>
          );
        }
        if (typeof val === "number" || val === null) {
          return (
            <NumberField
              key={key}
              label={key.replace(/_/g, " ")}
              value={val}
              onChange={(nv) => set({ [key]: nv })}
              min={0}
              disabled={fDisabled}
            />
          );
        }
        if (Array.isArray(val)) {
          return (
            <Field key={key} label={key.replace(/_/g, " ")}>
              <input
                className="as-input"
                value={val.join(", ")}
                placeholder="comma-separated"
                disabled={fDisabled}
                onChange={(e) =>
                  set({ [key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })
                }
              />
            </Field>
          );
        }
        return (
          <Field key={key} label={key.replace(/_/g, " ")}>
            <input
              className="as-input"
              value={String(val)}
              disabled={fDisabled}
              onChange={(e) => set({ [key]: e.target.value })}
            />
          </Field>
        );
      })}
    </div>
  );
}
